import React from 'react';
import clsx from 'clsx';
import throttle from 'lodash-es/throttle';
import ms from 'ms';
import type { MintSchedule } from '@soundxyz/sdk';
import { getMintExpiry, isScheduleSoldOut } from '@components/Song/MintComponents/helpers';
import { isOpenEdition } from '@components/Song/MintComponents/shared-helpers';
import { EligibleBadge } from '@components/basics/Badges/Eligible';
import { FormatStrictCoundown } from '@components/basics/FormatStrictCountdown';
import {
  type Action,
  QuantityInput,
  type State,
} from '@components/main-components/inputs/QuantityInput';
import { useSubscribeReleaseCollector } from '@hooks/useSubscribeRelease';
import { getSecondsFromExpiry, getTimeFromSeconds } from '@hooks/useTimer';
import { useEditionEligibleQuantity } from '@store/sdk/reactQuery';
import { bigIntToNumber } from '@utils/bigNumberHelpers';
import { ADDRESS_BATCH_MINT_LIMIT } from '@utils/constants';
import { formatWeiToEthDisplayFree, preciseMillify } from '@utils/helpers';
import {
  checkShowUrgency,
  getMaxSupplyWithRangeExpiredCheck,
  getPanelSubText,
  hasScheduleRangeExpired,
} from './helpers';

export function Panel({
  schedule,
  panelKey,
  selected,
  state,
  dispatch,
  hasSaleAccess,
  hasScheduleEligibility,
  onClick,
  chainId,
  releaseId,
  saleStarted,
  rootReleaseTotalMinted,
  saleEnded,
  finalQuantity,
  editionAddress,
}: {
  schedule: MintSchedule;
  panelKey: string;
  selected: boolean;
  state: State;
  dispatch: React.Dispatch<Action>;
  hasSaleAccess: boolean;
  hasScheduleEligibility: boolean;
  onClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  chainId: number;
  releaseId: string;
  saleStarted: boolean;
  rootReleaseTotalMinted: number;
  saleEnded: boolean;
  finalQuantity: number;
  editionAddress: string;
}) {
  const isPresale = schedule.mintType === 'MerkleDrop';

  const mintedCount = isPresale ? schedule.totalMinted : rootReleaseTotalMinted;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, forceUpdate] = React.useState(0);

  const mintExpiryMs = getMintExpiry(schedule);

  const rangeExpiryCheck = hasScheduleRangeExpired({
    schedule,
    totalMinted: mintedCount,
  });
  const maxSupply = getMaxSupplyWithRangeExpiredCheck({
    schedule,
    totalMinted: mintedCount,
  });

  const openEdition = (() => {
    //if open edition does not hit min on cutoff time, we treat as fixed
    if (rangeExpiryCheck) return false;

    return isOpenEdition(maxSupply);
  })();

  const maxQuantity = maxSupply ?? finalQuantity;

  const saleSoldOut = isScheduleSoldOut({
    schedule,
    collected: mintedCount,
  });

  const { data: eligibleQuantity = null, refetch: refetchEligibleQuantity } =
    useEditionEligibleQuantity({
      mintSchedule: schedule,
      chainId,
      editionAddress,
    });

  const throttledRefetchEligibleQuantity = React.useMemo(
    () =>
      throttle(refetchEligibleQuantity, ms('10 seconds'), {
        leading: true,
        trailing: true,
      }),
    [refetchEligibleQuantity],
  );

  useSubscribeReleaseCollector({
    releaseId,
    onData() {
      throttledRefetchEligibleQuantity();
    },
    enabled: true,
  });

  const inputMax = eligibleQuantity
    ? Math.min(bigIntToNumber(eligibleQuantity, { strict: false }), ADDRESS_BATCH_MINT_LIMIT)
    : 1;

  const { maxMintablePerAccount, startTime, price } = schedule;

  const maxText = `Max ${preciseMillify({
    value: maxMintablePerAccount,
    precision: 4,
  })}`;

  const panelSubText = getPanelSubText({
    hasSaleAccess,
    hasScheduleEligibility,
    saleEnded,
    soldOut: saleSoldOut,
  });

  const activeSale = saleStarted && !saleEnded && !saleSoldOut;

  const countdownInfo: CountdownType = React.useMemo(() => {
    if (saleStarted) {
      const { days } = getTimeFromSeconds(getSecondsFromExpiry(mintExpiryMs));
      return {
        expiryTimestamp: mintExpiryMs,
        label: days > 0 ? 'Ends' : 'Ends in',
      };
    } else {
      const { days } = getTimeFromSeconds(getSecondsFromExpiry(startTime));
      const action = 'Starts';
      return {
        expiryTimestamp: startTime,
        label: days > 0 ? action : `${action} in`,
      };
    }
  }, [mintExpiryMs, saleStarted, startTime]);

  const showUrgency = checkShowUrgency({
    hasSaleAccess,
    hasScheduleEligibility,
    activeSale,
    saleStarted,
  });

  return (
    <div
      className={clsx(
        'flex items-center justify-between rounded-md p-3 data-[selected=false]:ring-1 data-[selected=true]:ring-[3px] data-[selected=false]:ring-base300 data-[selected=true]:ring-base900  md:p-4',
        hasSaleAccess && activeSale
          ? 'cursor-pointer data-[selected=false]:bg-white data-[selected=true]:bg-base100 '
          : 'cursor-not-allowed opacity-50',
      )}
      data-selected={selected}
      onClick={onClick}
    >
      <div className="flex flex-col gap-1.5 md:gap-1">
        <div className="flex gap-1.5">
          <h3 className="font-title text-title-s font-medium text-base900 md:text-title-m">
            {isPresale ? 'Presale' : 'Public'}
          </h3>
          {isPresale && <EligibleBadge isEligible={hasScheduleEligibility} />}
        </div>
        <div className="flex flex-col items-start gap-0.5 font-base text-base-s">
          <div className="flex items-center gap-0.5">
            <p className="font-semibold text-base900">
              {formatWeiToEthDisplayFree({
                weiValue: price,
                maxDecimals: 5,
              })}
            </p>
            &nbsp;
            <span className="font-medium text-base400">•</span>
            &nbsp;
            <p className="text-base400">{maxText}</p>
          </div>

          {showUrgency ? (
            <p className="font-medium text-base400">
              {openEdition ? (
                <Countdown
                  expiryTimestamp={countdownInfo.expiryTimestamp}
                  label={countdownInfo.label}
                  onExpire={() => {
                    setTimeout(() => {
                      forceUpdate(prev => prev + 1);
                    }, 1000);
                  }}
                />
              ) : (
                <>
                  {mintedCount}/{maxQuantity} minted
                </>
              )}
            </p>
          ) : (
            !!panelSubText && <p className="font-medium text-base400">{panelSubText}</p>
          )}
        </div>
      </div>
      {selected && (
        <QuantityInput
          key={panelKey}
          state={state}
          disabled={!saleStarted || !hasSaleAccess}
          min={1}
          dispatch={dispatch}
          max={inputMax}
        />
      )}
    </div>
  );
}

type CountdownType = {
  expiryTimestamp: number;
  label: string;
  onExpire?: () => void;
};

const Countdown = ({ expiryTimestamp, label }: CountdownType) => {
  return (
    <div className="flex items-center">
      {label}&nbsp;
      <FormatStrictCoundown timeStamp={expiryTimestamp} expandedFormat={false} />
    </div>
  );
};
